// HR Dashboard card: at-a-glance personnel alerts for the Instructors page.
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ShieldCheck, FileText } from "lucide-react";

type Alerts = {
  w9Missing: number;
  cprExpiring: number;
  backgroundExpiring: number;
  ready1099: number;
  sent1099: number;
};

export default function HrDashboardCard() {
  const [alerts, setAlerts] = useState<Alerts | null>(null);

  useEffect(() => {
    (async () => {
      const year = new Date().getFullYear();
      const [instructors, docs, taxRecs] = await Promise.all([
        supabase.from("instructors")
          .select("id,w9_status,status")
          .neq("status", "merged"),
        (supabase.from as any)("instructor_documents")
          .select("instructor_id,doc_type,expires_at,is_current,deleted_at")
          .eq("is_current", true).is("deleted_at", null),
        (supabase.from as any)("instructor_tax_records")
          .select("instructor_id,tax_year,draft_status,approved_at,date_1099_issued,total_payments_cents")
          .eq("tax_year", year),
      ]);

      const active = (instructors.data ?? []).filter((i: any) => String(i.status ?? "").toLowerCase() === "active");
      const docsArr = (docs.data ?? []) as any[];
      const recs = (taxRecs.data ?? []) as any[];

      const hasDoc = (id: string, type: string) => docsArr.some((d) => d.instructor_id === id && d.doc_type === type);
      const nearExpiry = (id: string, type: string) => {
        const d = docsArr.find((x) => x.instructor_id === id && x.doc_type === type && x.expires_at);
        if (!d) return false;
        const days = Math.round((new Date(d.expires_at).getTime() - Date.now()) / (24 * 3600 * 1000));
        return days <= 60;
      };

      const w9Missing = active.filter((i: any) => !["Received", "Verified", "On File"].includes(String(i.w9_status ?? ""))).length;
      const cprExpiring = active.filter((i: any) => nearExpiry(i.id, "cpr_certification")).length;
      const backgroundExpiring = active.filter((i: any) => nearExpiry(i.id, "background_check")).length;
      const ready1099 = recs.filter((r) => r.approved_at && !r.date_1099_issued).length;
      const sent1099 = recs.filter((r) => !!r.date_1099_issued).length;

      setAlerts({ w9Missing, cprExpiring, backgroundExpiring, ready1099, sent1099 });
    })();
  }, []);

  const rows = useMemo(() => {
    if (!alerts) return [];
    return [
      { key: "w9", label: "W-9s Missing", value: alerts.w9Missing, tone: alerts.w9Missing > 0 ? "red" : "gray" },
      { key: "cpr", label: "CPR Certifications Expiring (≤60d)", value: alerts.cprExpiring, tone: alerts.cprExpiring > 0 ? "amber" : "gray" },
      { key: "bg", label: "Background Checks Expiring (≤60d)", value: alerts.backgroundExpiring, tone: alerts.backgroundExpiring > 0 ? "amber" : "gray" },
      { key: "r1099", label: "1099s Ready to Send", value: alerts.ready1099, tone: alerts.ready1099 > 0 ? "amber" : "gray" },
      { key: "s1099", label: "1099s Sent", value: alerts.sent1099, tone: "gray" },
    ];
  }, [alerts]);

  if (!alerts) return null;
  const anyRed = rows.some((r) => r.tone === "red" && r.value > 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          {anyRed ? <AlertTriangle className="h-4 w-4 text-red-600" /> : <ShieldCheck className="h-4 w-4 text-green-700" />}
          Personnel Alerts
        </CardTitle>
        <CardDescription>Live view of HR readiness across active instructors.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {rows.map((r) => (
            <div key={r.key} className="rounded-md border p-3">
              <div className="text-xs text-muted-foreground">{r.label}</div>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-2xl font-semibold">{r.value}</span>
                {r.tone === "red" && r.value > 0 && <Badge variant="destructive">action needed</Badge>}
                {r.tone === "amber" && r.value > 0 && <Badge className="bg-amber-500 hover:bg-amber-600">soon</Badge>}
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">
          <FileText className="h-3 w-3 inline mr-1" /> Open any instructor's Personnel File tab to act on these items.
        </p>
      </CardContent>
    </Card>
  );
}
